import type { Envelope } from "@/types/api";
import { api, http, unwrap } from "./client";

/** A tenant's white-label settings as the Super Admin edits them. "" = default. */
export interface TenantBranding {
  id?: string;
  tenant_id: string;
  browser_title: string;
  company_display_name: string;
  logo_url: string;
  favicon_url: string;
  login_title: string;
  login_subtitle: string;
  primary_color: string;
  secondary_color: string;
  login_background_color: string;
  login_background_image_url: string;
  support_email: string;
  support_phone: string;
  updated_at?: string;
}

/** The editable text/colour part - the PUT body. Images use the asset routes. */
export type BrandingTextInput = Pick<
  TenantBranding,
  | "browser_title"
  | "company_display_name"
  | "login_title"
  | "login_subtitle"
  | "primary_color"
  | "secondary_color"
  | "login_background_color"
  | "support_email"
  | "support_phone"
>;

export type BrandingAssetKind = "logo" | "favicon" | "login_background";

/** What GET /branding returns for the current hostname. */
export interface PublicBranding extends Omit<TenantBranding, "id" | "tenant_id" | "updated_at"> {
  configured: boolean;
  tenant_slug?: string;
  version: number;
}

export const brandingApi = {
  // ---- Super Admin (RequireSuperAdmin on the backend) ----
  get: (tenantId: string) => http.get<TenantBranding>(`/tenants/${tenantId}/branding`),
  update: (tenantId: string, input: BrandingTextInput) =>
    http.put<TenantBranding>(`/tenants/${tenantId}/branding`, input),
  uploadAsset: (tenantId: string, kind: BrandingAssetKind, file: File, onProgress?: (percent: number) => void) => {
    const form = new FormData();
    form.append("file", file);
    return unwrap(
      api.post<Envelope<TenantBranding>>(`/tenants/${tenantId}/branding/assets/${kind}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
        },
      }),
    );
  },
  removeAsset: (tenantId: string, kind: BrandingAssetKind) =>
    http.delete<TenantBranding>(`/tenants/${tenantId}/branding/assets/${kind}`),

  /**
   * The public, hostname-resolved branding. Plain fetch on purpose: no auth
   * header, no X-Tenant-ID, no token-refresh interceptor - the backend
   * decides the tenant from the Host header alone.
   */
  publicForHost: async (signal?: AbortSignal): Promise<PublicBranding | null> => {
    const base = import.meta.env.VITE_API_BASE_URL || "/api/v1";
    const res = await fetch(`${base}/branding`, { credentials: "omit", signal, cache: "no-cache" });
    if (!res.ok) return null;
    const body = (await res.json()) as Envelope<PublicBranding>;
    return body.success && body.data ? body.data : null;
  },
};
